// A session ID identifies one logged-in session of a user (login -> logout / token expiry).
// It is sent as X-PATHWAY-SESSION-ID on every backend request (see supportTracing in axios-helper)
// and attached to bug reports created via "Bug melden", so all transactions of the session in which
// a bug was reported can be found in the logs. In contrast, the backend trace UUID is per request
// and therefore cannot correlate a bug report with the requests that led to it.

import { clearOrganizationNamesCache } from "../admin/services/organizationNamesCache";

export const SESSION_ID_STORAGE_KEY = "pathway-session-id";

export const SESSION_ID_HEADER = "X-PATHWAY-SESSION-ID";

const generateSessionId = () => {
    if (typeof crypto !== "undefined") {
        if (typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        if (typeof crypto.getRandomValues === "function") {
            const bytes = crypto.getRandomValues(new Uint8Array(16));
            // Set the version (4) and variant bits as defined by RFC 4122
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
            return (
                hex.substring(0, 8) +
                "-" +
                hex.substring(8, 12) +
                "-" +
                hex.substring(12, 16) +
                "-" +
                hex.substring(16, 20) +
                "-" +
                hex.substring(20)
            );
        }
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};

// Starts a new session, replacing a session that is still in storage. Every login starts a new session
// (a login is a new principal, even in the same browser), a token refresh does not.
export const startSession = () => {
    const sessionId = generateSessionId();
    localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    return sessionId;
};

export const getSessionId = () => {
    return localStorage.getItem(SESSION_ID_STORAGE_KEY);
};

// Ends the session (logout, token expiry, forced re-login). The next login starts a new one.
export const clearSession = () => {
    localStorage.removeItem(SESSION_ID_STORAGE_KEY);
    clearOrganizationNamesCache();
};

export const sessionService = {
    startSession,
    getSessionId,
    clearSession,
};
